namespace $ {

	export type $shm_codex_api_thread = {
		id: string
		name?: string | null
		preview?: string
		cwd?: string
		modelProvider?: string
		createdAt?: number
		updatedAt?: number
		status?: any
		turns?: $shm_codex_api_turn[]
	}

	export type $shm_codex_api_turn = {
		id: string
		status: string
		startedAt?: number | null
		items: $shm_codex_api_item[]
	}

	export type $shm_codex_api_item = {
		id: string
		type: string
		text?: string
		content?: { type: string; text?: string }[]
		summary?: string[]
		command?: string
		aggregatedOutput?: string | null
		status?: string
	}

	export type $shm_codex_api_goal = {
		threadId: string
		objective: string
		status: string
		tokenBudget: number | null
		tokensUsed: number
		timeUsedSeconds: number
	}

	export class $shm_codex_api_error extends Error {
		constructor( public status: number, message: string ) {
			super( message )
		}
	}

	export class $shm_codex_api {
		static token() {
			const config = ( $ as any ).$shm_projects_config
			return String( config?.agent_token?.() ?? '' ).trim()
		}

		static url( base: string, path: string ) {
			return base.replace( /\/$/, '' ) + path
		}

		static async json( base: string, path: string, init?: RequestInit ) {
			const response = await fetch( this.url( base, path ), {
				... init,
				cache: 'no-store',
				headers: {
					'Content-Type': 'application/json',
					...( this.token() ? { 'X-Hitalama-Agent-Token': this.token() } : {} ),
					... init?.headers,
				},
			} )
			if( !response.ok ) {
				const data = await response.json().catch( ()=> ({}) )
				throw new $shm_codex_api_error( response.status, data.error || response.statusText )
			}
			if( response.status === 204 ) return null
			return response.json()
		}

		static sessions( base: string, stamp = 0, name_prefix = '' ) {
			const suffix = name_prefix ? `?namePrefix=${ encodeURIComponent( name_prefix ) }` : ''
			return this.json( base, `/sessions${ suffix }` ) as Promise< { data: $shm_codex_api_thread[] } >
		}

		static create_session( base: string, name: string, cwd: string, instructions = '' ) {
			return this.json( base, '/sessions', {
				method: 'POST',
				body: JSON.stringify({ name, cwd, instructions }),
			} ) as Promise< { thread: $shm_codex_api_thread } >
		}

		static thread( base: string, id: string, stamp = 0 ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }` ) as Promise< {
				thread: $shm_codex_api_thread
				activeTurnId: string | null
			} >
		}

		static send( base: string, id: string, text: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/messages`, {
				method: 'POST',
				body: JSON.stringify({ text }),
			} )
		}

		static stop( base: string, id: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/stop`, {
				method: 'POST',
				body: '{}',
			} )
		}

		static goal( base: string, id: string, stamp = 0 ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/goal` ) as Promise< {
				goal: $shm_codex_api_goal | null
			} >
		}

		static goal_set( base: string, id: string, objective: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/goal`, {
				method: 'PUT',
				body: JSON.stringify({ objective }),
			} )
		}

		static goal_clear( base: string, id: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/goal`, {
				method: 'DELETE',
			} )
		}

	}

	export class $shm_codex_api_events extends $mol_object {

		private source: EventSource | null = null

		url() { return '' }
		notify() { return ( event: any )=> {} }

		native() {
			if( this.source ) return this.source
			const events = new URL( $shm_codex_api.url( this.url(), '/events' ) )
			const token = $shm_codex_api.token()
			if( token ) events.searchParams.set( 'token', token )
			const source = this.source = new EventSource( events )
			source.onmessage = event => {
				try {
					this.notify()( JSON.parse( event.data ) )
				} catch( error ) {
					$mol_fail_log( error )
				}
			}
			return source
		}

		override destructor() {
			this.source?.close()
			this.source = null
			super.destructor()
		}

	}

}
